import { AfterViewInit, ChangeDetectorRef, Component, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DocumentManagerService,
    SyncService,
    getDocumentAppointmentAttachments,
    getDocumentAttachments,
    getDocumentComplaintSpareParts,
    getDocumentNoteAttachments,
    getDocumentPositions,
    getDocumentsAttachments,
    getSelected,
    getSelectedComplaint,
    getSelectedComplaintAppointments,
    getSelectedComplaintPositions,
    getSelectedComplaintSpareParts,
    getSelectedId,
    getSynced,
    openDoc,
} from '@icc/b2b/sales-document';
import {
    AssignPermission,
    ClockService,
    Complaint,
    ComplaintAppointment,
    ComplaintData,
    ComplaintNote,
    ComplaintPosition,
    ComplaintsDocumentType,
    ComplaintsDocumentTypes,
    ComplaintSparePart,
    DocumentAttachment,
    DocumentChange,
    editableAppointmentStatuses,
    openedAppointmentStatuses,
    PersonsService,
    ProductsList,
    State,
    Synced,
    Workflow,
} from '@icc/b2b/shared';
import { core, parseDate } from '@icc/shared/helpers';
import { Store } from '@ngrx/store';
import {
    Observable,
    ReplaySubject,
    Subscription,
    combineLatest,
    lastValueFrom,
    startWith,
} from 'rxjs';
import { ComplaintService } from '../complaint.service';
import { ConfigService, TranslateService } from '@icc/shared';
import { NgxPermissionsService } from 'ngx-permissions';
import { delay, filter, map, switchMap, take } from 'rxjs/operators';
import { DialogService, InputComponent } from '@icc/ui';
import { HttpClient } from '@angular/common/http';
import { ComplaintDocumentPreviewComponent } from '../complaint-document-preview/complaint-document-preview.component';
import { StatsService } from '@icc/shared/helpers/b2b';
import { ProductsService } from 'libs/b2b/trade/src/lib/products.service'
import { ComplaintAppointmentPreviewComponent } from '../complaint-appointment-preview/complaint-appointment-preview.component';
import { ComplaintUserPreviewDialogComponent } from '../complaint-user-preview-dialog/complaint-user-preview-dialog.component';
import { ComplaintParty } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintChangeUserDialogComponent } from '../complaint-change-user-dialog/complaint-change-user-dialog.component';

@Component({
    selector: 'icc-complaint',
    templateUrl: './complaint.component.html',
    styleUrls: ['./complaint.component.scss'],
})
export class ComplaintComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();
    documentUuid = '';

    positions$ = this.store.select(getSelectedComplaintPositions).pipe(
        map(
            (pos) =>
                pos.map((p) => ({
                    ...p,
                    configuration: p.configuration ? core.parseJson(p.configuration) : null,
                })) as ComplaintPosition[]
        )
    );

    positionsShowDetails = new Map<string, boolean>();
    positionsTitles = new Map<string, string[]>();
    positionsIndexes = new Map<string, string>();
    positionsWorkflows = new Map<string, Workflow | null>();
    positionsWorkflowsGroups = new Map<number, ComplaintPosition[]>();
    positionsToVerify: ComplaintPosition[] = [];
    multipleWorkflows = false;
    selectedPositions: string[] = [];

    complaintPositions: ComplaintPosition[] = [];

    complaintSpareParts$ = this.store.select(getSelectedComplaintSpareParts);
    complaintSpareParts: ComplaintSparePart[] = [];

    usersMap: Map<number, string> = new Map();

    appointments$ = this.store.select(getSelectedComplaintAppointments).pipe(
        map((appointments) =>
            appointments
                .slice()
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((a) => ({
                    ...a,
                })) as ComplaintAppointment[]
            )
    );

    complaintsAppointmentsMap: Map<string, ComplaintAppointment> = new Map<string, ComplaintAppointment>();
    complaintAppointments: ComplaintAppointment[] = [];

    productId = 0;
    productsList!: ProductsList;

    activeUserCompanyId: number;

    openedDocumentId$ = this.store.select(getSelectedId);
    openedDocument$ = this.store.select(getSelectedComplaint) as Observable<Complaint>;
    complaintsDocument$ = this.openedDocument$.pipe(
        map((document) =>
            document &&
            (Number(document.authorizedRetailerCompanyId) ===
                Number(this.configService?.user?.user?.ActiveCompany) ||
                Number(document.distributorCompanyId) ===
                    Number(this.configService?.user?.user?.ActiveCompany) ||
                document.formerDistributors?.[
                    Number(this.configService?.user?.user?.ActiveCompany)
                ] ||
                Number(document.manufacturerCompanyId) ===
                    Number(this.configService?.user?.user?.ActiveCompany))
                ? document
                : null
        )
    );

    openedDocuments: Record<string, Complaint> = {};

    complaintsPositionsMap: Map<string, ComplaintPosition> = new Map<string, ComplaintPosition>();

    attachments$ = this.openedDocument$.pipe(
        switchMap((document) => this.store.select(getDocumentsAttachments([document.uuid])))
    );

    appointmentAttachments$ = this.openedDocument$.pipe(
        switchMap((document) => this.store.select(getDocumentAppointmentAttachments([document.uuid])))
    );

    complaintNoteAttachments$ = this.openedDocument$.pipe(
        switchMap((document) => this.store.select(getDocumentNoteAttachments([document.uuid])))
    );

    allChanges: DocumentChange[] = [];

    changes: (
        | DocumentChange & {
              group?: DocumentChange[];
              positions?: ComplaintPosition[];
              appointments?: ComplaintAppointment[];
              complaintNotes?: ComplaintNote[];
              complaintSpareParts?: ComplaintSparePart[];
              attachments?: DocumentAttachment[];
              id: number;
          }
    )[] = [];

    changesLoading = true;

    syncedDocumentsState = new Map<string, Synced>();
    synced: Synced = Synced.Offline;
    complaintType: 'complaint' | 'service' = 'complaint';
    hasAccessTo = {
        manageComplaint: false,
        manageComplaintManufacturer: false,
        addComplaintNote: false,
        editAssign: false,
        scheduleAppointment: false,
    };

    private subscriptions: Subscription[] = [];

    origin: 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;

    showFullPositionTitles = false;

    documentTypes = ComplaintsDocumentTypes;
    preview = false;

    authorizedRetailerDistributorComplaint = false;
    distributorManufacturerComplaint = false;

    private scrollPending = false;

    constructor(
        private store: Store<State>,
        private route: ActivatedRoute,
        private complaintService: ComplaintService,
        private documentManagerService: DocumentManagerService,
        private configService: ConfigService,
        private router: Router,
        private translateService: TranslateService,
        private syncService: SyncService,
        private permissionsService: NgxPermissionsService,
        private http: HttpClient,
        private dialogService: DialogService,
        private chRef: ChangeDetectorRef,
        private statsService: StatsService,
        private productsService: ProductsService
    ) {
        this.origin = this.route.snapshot.queryParamMap.get('origin') as 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;
        this.activeUserCompanyId = Number(this.configService?.user?.user?.ActiveCompany);
        this.showFullPositionTitles = this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
    }

    async ngOnInit() {
        this.store.dispatch(openDoc({ id: this.route.snapshot.params.id }));
        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                this.store.dispatch(openDoc({ id: params.id }));
            }),
            combineLatest([this.complaintsDocument$.pipe(startWith(null))]).subscribe(
                ([complaintsDocument]) => {
                    const documents: Record<string, Complaint> = {};
                    const openedDocuments = [];
                    this.documentUuid = complaintsDocument?.uuid ?? '';
                    this.complaintType =
                        Number(complaintsDocument?.documentTypeId) === ComplaintsDocumentType.Service ? 'service' : 'complaint';
                    this.setDocumentCrumbs();
                    this.loadChanges();

                    if (complaintsDocument) {
                        documents[complaintsDocument.uuid] = complaintsDocument;
                        openedDocuments.push(complaintsDocument.uuid);
                    }

                    this.statsService.add({
                        action: 'open-complaint',
                    });

                    this.openedDocuments = documents;
                    this.syncService.setCurrentDocumentsIds(Array.from(new Set(openedDocuments)));
                }
            ),
            combineLatest([this.positions$]).subscribe(async ([positions]) => {
                const complaintsPositionsMap: Map<string, ComplaintPosition> = new Map();
                const positionsShowDetails = new Map<string, boolean>();
                const positionsTitles = new Map<string, string[]>();
                this.positionsToVerify = [];
                positions.forEach((position) => {
                    complaintsPositionsMap.set(position.uuid, position);
                    positionsShowDetails.set(
                        position.uuid,
                        this.positionsShowDetails.get(position.uuid) ?? true
                    );
                    positionsTitles.set(position.uuid, this.complaintService.getPositionTitle(position, this.showFullPositionTitles));
                    if (position.toVerify) {
                        this.positionsToVerify.push(position);
                    }
                });
                this.complaintsPositionsMap = complaintsPositionsMap;
                this.positionsShowDetails = positionsShowDetails;
                this.positionsTitles = positionsTitles;
                this.complaintPositions = positions;

            }),
            combineLatest([this.appointments$]).subscribe(async ([appointments]) => {
                await this.complaintService.loaded;
                const complaintsAppointmentsMap: Map<string, ComplaintAppointment> = new Map();
                appointments.forEach((appointment) => {
                    complaintsAppointmentsMap.set(appointment.uuid, appointment);
                });
                this.complaintsAppointmentsMap = complaintsAppointmentsMap;
                this.complaintAppointments = appointments;
                this.evaluateAppointmentAvailability();
            }),
            combineLatest([
                this.openedDocument$,
                this.openedDocumentId$,
                this.permissionsService.permissions$,
            ]).subscribe(([document, documentId]) => {
                if (!document && documentId) {
                    this.store.dispatch(openDoc({ id: null }));
                    this.router.navigate(['/trade']);
                }
            }),
            combineLatest([
                this.openedDocument$,
                this.positions$,
            ]).subscribe(([document, positions]) => {
                if (document) {
                    this.checkPositionsWorkflow(document, positions);
                    this.checkAccess();
                }
            }),
            this.complaintSpareParts$.subscribe((complaintSpareParts) => {
                this.complaintSpareParts = complaintSpareParts;
            }),
            this.store.select(getSynced).subscribe(({ synced }) => {
                this.syncedDocumentsState = synced;
                this.updatedSynced();
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
        this.authorizedRetailerDistributorComplaint = this.openedDocuments[this.documentUuid].clientName.length < 1 && this.openedDocuments[this.documentUuid].authorizedRetailerCompanyId !== null;
        this.distributorManufacturerComplaint = this.openedDocuments[this.documentUuid].clientName.length < 1 && this.openedDocuments[this.documentUuid].authorizedRetailerCompanyId === null;
        await this.loadProductsList();
    }

    ngAfterViewInit(): void {
        let top = document.getElementById('top');
        const appointmentUuid = this.route.snapshot.queryParamMap.get('appointmentUuid');
        if (appointmentUuid !== null) {
            if (!this.scrollPending) {
                this.scrollPending = true;

                this.appointments$.pipe(
                    filter(appointments => appointments && appointments.length > 0),
                    take(1),
                    delay(10)
                ).subscribe(() => {
                    const appointmentToScrollTo = document.getElementById(appointmentUuid);
                    if (appointmentToScrollTo !== null) {
                        this.scrollToSection('appointments');
                    }
                    this.scrollPending = false;
                });
            }
        } else if (top !== null) {
            top.scrollIntoView();
            top = null;
        }
    }

    ngOnChanges() {
        this.checkAccess();
    }

    ngOnDestroy() {
        this.subscriptions.map((el) => el.unsubscribe());
    }

    async checkAccess() {
        const currentDocument = this.openedDocuments[this.documentUuid];
        console.log('currentDocument', currentDocument);
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage') && ((currentDocument.assignedTo && currentDocument.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
        this.hasAccessTo.manageComplaintManufacturer = await this.permissionsService.hasPermission('complaints-manage-manufacturer');
        this.hasAccessTo.addComplaintNote = await this.permissionsService.hasPermission('complaints-manage');
        this.hasAccessTo.editAssign = await this.permissionsService.hasPermission('complaints-assign') && ((currentDocument.assignedTo && currentDocument.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
    }

    async evaluateAppointmentAvailability() {
        if (this.positionsWorkflowsGroups?.size === 1) {
            const documentStatusId = this.complaintService.getDocumentStatusForUser(this.openedDocuments[this.documentUuid], this.getUserType());
            const positionsWorkflow = this.complaintService.workflows.find(
                (el) => +el.id === +this.positionsWorkflowsGroups.keys().next().value
            );
            const rolesStatuses = (positionsWorkflow?.userRolesStatuses || []).filter((el) =>
                el.level === this.getUserType() &&
                el.userRolesIds.some(
                    (roleId) =>
                    (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(
                            Number(roleId)
                        ))
                )
            );
            if (!rolesStatuses.length) {
                this.hasAccessTo.scheduleAppointment = false;
                return;
            }
            const isAppointmentInProgress = this.complaintAppointments.some(
                (el) =>openedAppointmentStatuses.some(status => status.value === el.status) &&
                    +el.assignedCompanyId! === +(this.configService.user?.user?.ActiveCompany || 0)
            );
            this.hasAccessTo.scheduleAppointment = rolesStatuses.some(
                el => el.buttons[documentStatusId]?.create_appointment?.available
            ) && !isAppointmentInProgress;
        }
    }

    scrollToTop() {
        const top = document.getElementById('top');
        if (top !== null) {
            top.scrollIntoView({ behavior: 'smooth' });
        }
    }

    scrollToSection(section: string) {
        const element = document.getElementById(section);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    updatedSynced() {
        this.synced = this.syncService.getSynced([this.documentUuid], this.syncedDocumentsState);
        if (this.synced === Synced.Synced) {
            this.loadChanges();
        }
    }

    setDocumentCrumbs() {
        const crumbs = [];
        let label = '';
        const commands = [
            'trade',
            'complaint',
            'list',
            (this.origin === 'appointment-list' || this.origin === 'appointment-map') ? 'appointments' : 'complaints',
        ];
        label =
        (this.origin === 'appointment-list' ? this.translateService.instant('COMPLAINT|Lista wizyt') :
        (this.origin === 'appointment-map' ? this.translateService.instant('COMPLAINT|Mapa wizyt') :
        (!this.origin || this.origin === 'list' ? this.translateService.instant('COMPLAINT|Lista reklamacji') : this.translateService.instant('COMPLAINT|Mapa reklamacji'))
        ));
        crumbs.push({
            label,
            url: '/' + commands.join('/'),
            origin: this.origin,
        });
        ComplaintComponent.crumbsSubject.next({
            level: 1,
            crumbs,
        });
        if (this.documentUuid) {
            label = this.translateService.instant('COMPLAINT|Obsługa zgłoszenia reklamacyjnego');
            ComplaintComponent.crumbsSubject.next({
                level: 2,
                crumbs: [
                    {
                        label,
                        url: '/trade/complaint/' + this.documentUuid,
                    },
                ],
            });
        }
    }

    switchShowDetails(posId: ComplaintPosition['uuid'], showDetails: boolean) {
        this.positionsShowDetails.set(posId, showDetails);
    }

    trackByPosition(index: number, el: ComplaintPosition) {
        return el.uuid;
    }

    trackByAppointment(index: number, el: ComplaintAppointment) {
        return el.uuid;
    }

    async loadChanges() {
        if (this.documentUuid) {
            const result = await lastValueFrom(
                this.http.get<{
                    changes: DocumentChange[];
                }>('/api/b2b/document-history/' + this.documentUuid + '?groupingSchemaId=0')
            );
            if (result.changes) {
                this.changesLoading = false;
                this.allChanges = result.changes;
                await this.groupChanges();
                this.changes.reverse();
                this.chRef.detectChanges();
            }
        }
    }

    async groupChanges() {
        this.changes = [];
        let addDocChange;
        let i = 0;
        for (const change of this.allChanges) {
            if (change.type === 'addDoc') {
                addDocChange = change;
                this.changes.push({
                    ...change,
                    id: i++,
                    positions: [],
                    attachments: [],
                });
            } else if (change.type === 'addPosition') {
                addDocChange = this.changes.find((el) => el.type === 'addDoc');
                if (addDocChange?.positions) {
                    addDocChange.positions.push(change.value);
                }
            } else if (
                change.type === 'addAttachment' &&
                ((parseDate(change.date).getTime() - parseDate(addDocChange?.date).getTime()) /
                    1000 <
                    5)
            ) {
                addDocChange = this.changes.find((el) => el.type === 'addDoc');
                if (addDocChange?.attachments) {
                    addDocChange.attachments.push(change.value);
                }
            } else if (
                change.type === 'removeAttachment' &&
                this.changes.some(
                    (el) =>
                        el.type === 'setPositionField' &&
                        el.field === 'isDeleted' &&
                        change.positionId === el.positionId &&
                        (parseDate(change.date).getTime() - parseDate(el?.date).getTime()) / 1000 <
                            1
                )
            ) {
                continue;
            } else if (change.type === 'setPositionField' && change.field === 'isDeleted') {
                this.changes.push({
                    ...change,
                    id: i++,
                    description: '<a href="/b2b/trade/complaint/' + change.value + '">' + change.description + '</a>',
                    group: [],
                    html: true,
                });
            } else if (change.type === 'setPositionField' && change.field === 'configuration') {
                const defectPlace: string[] = [];
                this.showFullPositionTitles ? this.complaintService.getFullDefectPlace(defectPlace, change.description) : this.complaintService.getDefectPlace(defectPlace, change.description);
                this.changes.push({
                    ...change,
                    id: i++,
                    description: defectPlace.join(' > '),
                    group: [],
                });
            } else if (change.type === 'addComplaintNote') {
                this.changes.push({
                    ...change,
                    id: i++,
                    group: [],
                });
            } else if (change.type === 'addAppointment') {
                this.changes.push({
                    ...change,
                    id: i++,
                    group: [],
                });
            } else if (change.type === 'setField' && change.field === 'complaintOrderNumber' && !(
                await this.permissionsService.hasPermission('complaints-manage') &&
                this.configService.user?.user?.ActiveCompanyLicenseType === 'license'
            )) {
                continue;
            } else if (change.type === 'changedStatus' && change.field === 'manufacturerStatusId' && this.getUserType() === 'authorizedRetailer') {
                continue;
            } else if (change.type === 'setPositionField' && (change.field === 'distributorOrderNumber' || change.field === 'distributorOrderPositionNumber') && this.getUserType() !== 'manufacturer') {
                continue;
            } else {
                if (
                    this.changes[this.changes.length - 1]?.group &&
                    change.type === this.changes[this.changes.length - 1]?.type &&
                    (!change?.positionId ||
                        (change?.positionId &&
                            this.changes[this.changes.length - 1]?.positionId ===
                                change?.positionId)) &&
                    this.changes[this.changes.length - 1]?.userId === change?.userId &&
                    !this.changes[this.changes.length - 1]?.group?.some(
                        (el) => el.field && change.field && el.field === change.field
                    ) &&
                    (parseDate(change.date).getTime() -
                        parseDate(this.changes[this.changes.length - 1]?.date).getTime()) /
                        1000 <
                        120
                ) {
                    this.changes[this.changes.length - 1]?.group?.unshift(change);
                } else {
                    this.changes.push({
                        ...change,
                        id: i++,
                        group: [change],
                    });
                }
            }
        }
    }

    showDocumentFirstVersion(
        change: DocumentChange & {
            positions: ComplaintPosition[];
            attachments: DocumentAttachment[];
        }
    ) {
        this.dialogService.open(ComplaintDocumentPreviewComponent, {
            data: {
                document: core.copy(change.value),
                positions: change.positions,
                attachments: change.attachments,
            },
        });
    }

    showAppointmentFirstVersion(
        change: DocumentChange
    ) {
        this.dialogService.open(ComplaintAppointmentPreviewComponent, {
            data: {
                appointment: core.copy(change.value),
                document: core.copy(this.openedDocuments[change.value.documentId]),
            }
        });
    }

    trackByFunction(index: number | string, item): number | string {
        return item.id;
    }

    changeComplaintType(documentTypeId: ComplaintsDocumentType) {
        this.complaintType = Number(documentTypeId) === ComplaintsDocumentType.Service ? 'service' : 'complaint';
        this.setDocumentCrumbs();
    }

    async checkPositionsWorkflow(document: Complaint, positions: ComplaintPosition[]) {
        const {positionsWorkflows, positionsWorkflowsGroups} = await this.complaintService.getPositionsWorkflows(document, positions);
        const positionsGroups = this.complaintService.groupPositions(positionsWorkflowsGroups);
        this.positionsWorkflows = positionsWorkflows;
        this.positionsWorkflowsGroups = positionsWorkflowsGroups;
        this.multipleWorkflows = positionsGroups.size > 1;
        if (this.openedDocuments[this.documentUuid] && this.openedDocuments[this.documentUuid].multipleWorkflows !== this.multipleWorkflows) {
            this.complaintService.changeMultipleWorkflows(this.openedDocuments[this.documentUuid], this.multipleWorkflows);
        }
        if (this.openedDocuments[this.documentUuid]) {
            this.evaluateAppointmentAvailability();
        }
    }

    selectPosition(positionId: string) {
        this.selectedPositions.push(positionId);
    }

    unselectPosition(positionId: string) {
        this.selectedPositions = this.selectedPositions.filter((el) => el !== positionId);
    }

    clearSelectedPositions() {
        this.selectedPositions = [];
    }

    editAppointment(complaint: Complaint) {
        const complaintAppointment = {
            uuid: core.generateUUID(),
            documentId: complaint.uuid,
            machineId: ClockService.getClock().timestamp.node(),
            date: null,
            time: null,
            scheduledTime: null,
            timeUsed: null,
            status: 'proposed',
            comment: '',
            assignedUserId: null,
            protocolNotes: '',
            protocolDate: null,
            protocolChangedPartsOrActions: '',
            paidService: false,
            repairCost: null,
            workStatus: 'inProgress',
            workToDo: '',
            protocolUserNotes: '',
            serviceRating: 0,
            orderCompliance: false,
            wingDamages: false,
            roof: false,
            properDoorLocking: false,
            doorAdjustment: false,
            doorMeasurement: false,
        } as ComplaintAppointment;
        this.complaintService.editAppointment(complaint, complaintAppointment, [], false, this.positionsWorkflowsGroups);
    }

    editComplaint(complaint: Complaint) {
        this.complaintService.editComplaint(complaint, this.complaintAppointments, this.positionsWorkflowsGroups, this.complaintPositions);
    }

    manageComplaintSpareParts(complaint: Complaint) {
        this.complaintService.manageComplaintSpareParts(complaint);
    }

    createComplaintNote(complaint: Complaint) {
        const complaintNote = {
            uuid: core.generateUUID(),
            documentId: complaint.uuid,
            positionId: null,
            machineId: ClockService.getClock().timestamp.node(),
            content: '',
            visibility: 'private',
            authorCompanyId: Number(this.configService?.user?.user?.ActiveCompany),
            authorUserId: Number(this.configService?.user?.user?.id),
            replyId: '',
        } as ComplaintNote;
        this.complaintService.createComplaintNote(complaint, complaintNote, this.complaintsPositionsMap, []);
    }

    replyToComplaintNote(
        complaint: Complaint,
        complaintNote: ComplaintNote,
    ) {
        const complaintNoteReply = {
            uuid: core.generateUUID(),
            documentId: complaintNote.documentId,
            positionId: complaintNote.positionId,
            machineId: ClockService.getClock().timestamp.node(),
            content: '',
            visibility: complaintNote.visibility,
            authorCompanyId: Number(this.configService?.user?.user?.ActiveCompany),
            authorUserId: Number(this.configService?.user?.user?.id),
            replyId: complaintNote.uuid,
        } as ComplaintNote;
        this.complaintService.createComplaintNote(complaint, complaintNoteReply, this.complaintsPositionsMap, [], true);
    }

    getProductName(productId: number) {
        return this.productsList.find(product => product.id === productId)?.name;
    }

    private async loadProductsList() {
        await this.productsService.loadData().then(() =>
            this.productsService.getData().then((productData) => {
                this.productsList = productData.productsList;
            })
        );
    }

    getUnit(productId: number) {
        return this.productsList.find(product => product.id === productId)?.price.priceUnits.find(unit => {
            const part = this.complaintSpareParts.find(sparePart => sparePart.productId === productId);
            return unit.id === part?.unitId && unit.code === part?.unitCode;
        });
    }

    removeSparePartFromComplaint(complaintSparePart: ComplaintSparePart) {
        this.complaintService.deleteComplaintSparePart(complaintSparePart);
    }

    getStatus(statusId: number) {
        return this.complaintService.getStatus(statusId);
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    openUserPreviewDialog(userType: string, document: Complaint) {
        const user = this.getUserByType(userType, document);

        const responsibleUser = {
            name: document[userType as 'authorizedRetailer' | 'distributor' | 'manufacturer']?.referenceName,
            phoneNumber: document[userType as 'authorizedRetailer' | 'distributor' | 'manufacturer']?.phoneNumber,
            email: document[userType as 'authorizedRetailer' | 'distributor' | 'manufacturer']?.email,
        }

        const assignedUser = {
            name: document[userType + 'UserName' as 'authorizedRetailerUserName' | 'distributorUserName' | 'manufacturerUserName'],
            phoneNumber: document[userType + 'PhoneNumber' as 'authorizedRetailerPhoneNumber' | 'distributorPhoneNumber' | 'manufacturerPhoneNumber'],
            email: document[userType + 'Email' as 'authorizedRetailerEmail' | 'distributorEmail' | 'manufacturerEmail'],
        }

        this.dialogService
            .open(ComplaintUserPreviewDialogComponent, {
                data: {
                    user,
                    userType,
                    responsibleUser,
                    assignedUser,
                },
            })
            .afterClosed();
    }

    getUserByType(userType: string, document: Complaint): ComplaintParty | undefined {
        return this.complaintService.getUserByType(userType, document);
    }

    canChangeUser(userType: string) {
        if (this.hasAccessTo.editAssign) {
            return this.complaintService.canChangeUser(userType);
        }
        return false;
    }

    openChangeUserDialog(userType: string, document: Complaint) {
            const user = this.getUserByType(userType, document);

            this.dialogService
                .open(ComplaintChangeUserDialogComponent, {
                    data: {
                        user,
                        userType,
                        companyId: this.configService.user?.user?.ActiveCompanyLicenseType === 'license'
                            ? userType === 'authorizedRetailer'
                                ? document.distributorCompanyId
                                : this.configService.user?.company?.id
                            : document.distributorCompanyId,
                        chooseContractorContractor:
                            this.configService.user?.user?.ActiveCompanyLicenseType === 'license' &&
                            userType === 'authorizedRetailer',
                    },
                })
                .afterClosed()
                .subscribe((data) => {
                    if (data) {
                        this.complaintService.changeUser(document, data, userType);
                    }
                });
        }
}
